import { Pagination, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { ReactNode } from 'react';

import { Select } from '../v2/Select';
import classNames from './DataTablePagination.module.scss';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type PaginationRenderState = {
  page: number;
  totalPages: number;
  totalRecords: number | undefined;
  recordsPerPage: number | undefined;
  from?: number;
  to?: number;
};

type PaginationRenderActions = {
  setPage: (page: number) => void;
  setRecordsPerPage?: (count: number) => void;
};

export type PaginationTextRenderer = (params: {
  from: number;
  to: number;
  totalRecords: number;
}) => ReactNode;

type Props = {
  state: PaginationRenderState;
  actions: PaginationRenderActions;
  recordsPerPageOptions?: number[];
  paginationText?: PaginationTextRenderer;
};

export function DataTablePagination({
  state,
  actions,
  recordsPerPageOptions,
  paginationText,
}: Props) {
  const {
    page,
    totalPages,
    totalRecords = 0,
    recordsPerPage,
    from = 0,
    to = 0,
  } = state;
  const { setPage, setRecordsPerPage } = actions;

  const sizeOptions = recordsPerPageOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  // Consumer-provided paginationText takes over the left slot (it usually
  // bundles its own page-size selector). Otherwise show the default summary
  // plus a built-in selector when the table is wired with onRecordsPerPageChange.
  const leftContent = paginationText ? (
    paginationText({ from, to, totalRecords })
  ) : (
    <>
      <span className={classNames.summary}>
        {from}-{to} / {totalRecords}
      </span>
      {setRecordsPerPage && recordsPerPage !== undefined && (
        <Select
          size="xs"
          w={120}
          allowDeselect={false}
          value={String(recordsPerPage)}
          data={sizeOptions.map((size) => ({
            value: String(size),
            label: `${size} per page`,
          }))}
          onChange={(val) => {
            if (val) setRecordsPerPage(Number(val));
          }}
        />
      )}
    </>
  );

  return (
    <div className={classNames.pagination}>
      <div className={classNames.left}>{leftContent}</div>
      <div className={classNames.right}>
        <UnstyledButton
          className={classNames.navButton}
          onClick={() => canGoPrev && setPage(page - 1)}
          disabled={!canGoPrev}
          aria-label="Previous page"
          data-disabled={!canGoPrev}
        >
          <IconChevronLeft size={16} stroke={2} />
        </UnstyledButton>
        <Pagination
          total={totalPages}
          value={page}
          onChange={setPage}
          siblings={1}
          boundaries={1}
          withControls={false}
          withEdges={false}
          classNames={{
            root: classNames.pages,
            control: classNames.pageItem,
            dots: classNames.pageDots,
          }}
        />
        <UnstyledButton
          className={classNames.navButton}
          onClick={() => canGoNext && setPage(page + 1)}
          disabled={!canGoNext}
          aria-label="Next page"
          data-disabled={!canGoNext}
        >
          <IconChevronRight size={16} stroke={2} />
        </UnstyledButton>
      </div>
    </div>
  );
}
